import {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getUserInfo, refreshToken } from "../services/api";
import { getFromStorage, removeFromStorage, setInStorage, userKey } from "../services/storage";
import { dataDeepCopy } from "../services/utils";
import { UserActionType, UserContextType, UserType } from "../types/user";

const initialUser: UserType = {
  accessToken: "",
  page: null,
  pageParams: null,
};

const UserContext = createContext({} as UserContextType);
const UserDispatchContext = createContext(null as unknown as Dispatch<UserActionType>);

const userReducer = (state: UserType, action: UserActionType) => {
  switch (action.type) {
    case "set-data": {
      return {
        ...state,
        accessToken: action.payload.accessToken,
        profile: action.payload.profile,
      };
    }
    case "set-access-token": {
      return {
        ...state,
        accessToken: action.payload,
      };
    }
    case "set-profile":
      return {
        ...state,
        profile: action.payload,
      };
    case "set-from-storage": {
      const payload = { ...action.payload };
      if (!payload.accessToken) {
        payload.accessToken = null;
      }
      return {
        ...state,
        ...payload,
      };
    }
    case "set-page": {
      const st = { ...state };
      st.page = action.payload.page;
      st.pageParams = action.payload.pageParams;

      return st;
    }
    case "clear":
      return initialUser;
    default:
      return state;
  }
};

export const UserProvider = ({ children }: PropsWithChildren) => {
  const [user, dispatch] = useReducer(userReducer, initialUser);

  useEffect(() => {
    const isValid = checkExpiredToken();
    if (user.accessToken?.length && !isValid) {
      logout();
    }
  }, [user]);

  const populateFromStorage = () => {
    const localUser = getFromStorage(userKey, initialUser);
    const data = dataDeepCopy(localUser);

    if (data) {
      dispatch({ type: "set-from-storage", payload: data });
    }
  };
  const saveToStorage = () => {
    setInStorage(userKey, user);
  };
  const checkExpiredToken = () => {
    if (!user.accessToken) {
      return false;
    }

    try {
      const token = JSON.parse(atob(user.accessToken.split(".")[1]));
      if (token && Math.floor(Date.now() / 1000) >= token.exp) {
        return false;
      }
    } catch (e) {
      console.log(e);
      return false;
    }

    return true;
  };
  const fetchUserInfo = async (accessToken: string) => {
    try {
      const data = await getUserInfo(accessToken);
      dispatch({
        type: "set-profile",
        payload: data,
      });
      return data;
    } catch (e) {
      return console.log(e);
    }
  };
  const refreshAccessToken = async () => {
    try {
      const data = await refreshToken();
      dispatch({
        type: "set-access-token",
        payload: data.accessToken,
      });
      return data;
    } catch (e) {
      console.log(e);
      logout();
      return;
    }
  };
  const logout = () => {
    dispatch({ type: "clear" });
    removeFromStorage(userKey);
    const redirectUrl = "/";
    window.location.href = redirectUrl;
  };

  const obj = {
    user,
    populateFromStorage,
    saveToStorage,
    fetchUserInfo,
    checkExpiredToken,
    refreshAccessToken,
    logout,
  };

  return (
    <UserContext.Provider value={obj}>
      <UserDispatchContext.Provider value={dispatch}>{children}</UserDispatchContext.Provider>
    </UserContext.Provider>
  );
};

export function useUserContext() {
  return useContext(UserContext);
}

export function useUserDispatchContext() {
  return useContext(UserDispatchContext);
}

export function RequireUserAuth({ children }: PropsWithChildren) {
  const { user, checkExpiredToken, refreshAccessToken } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Store location.pathname in a ref or state to access previous URL
    console.log("LOCATION", location);
  }, [location]);

  useEffect(() => {
    if (user.accessToken === undefined) {
      return;
    }
    if (user.accessToken === null) {
      navigate("/login");
      return;
    }
    const isValid = checkExpiredToken();
    console.log("auth check, isValid:", isValid);
    if (!isValid) {
      refreshAccessToken();
    }
  }, [user.accessToken]);

  return <>{children}</>;
}
